import React, { useEffect, useState } from 'react';

import { Link } from '~/lib/react-router-dom';

import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VideoCallOutlinedIcon from '@mui/icons-material/VideoCallOutlined';
import { Box, Button, Divider, IconButton, Paper, Stack, Typography } from '@mui/material';

import Image from '~/components/Common/Image';

import type { MediaImage } from './ImagesVideo';

interface SupportingAssetHeaderAction {
  to?: string;
}

interface Props {
  propertyId?: string;
  propertyLogoId?: string;
  vendorId?: string;
  videoId?: string;
  videoPosterImage?: MediaImage;
}

function renderSupportingAssetHeader(title: string, actionLabel: string, action?: SupportingAssetHeaderAction) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      justifyContent="space-between"
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      spacing={2}
      sx={{ px: 3, py: 2.5 }}
    >
      <Typography variant="h5">{title}</Typography>
      {action?.to ? (
        <Button
          component={Link}
          to={action.to}
          variant="outlined"
          startIcon={<UploadFileIcon />}
          target="_blank"
          rel="noopener noreferrer"
        >
          {actionLabel}
        </Button>
      ) : (
        <Button variant="outlined" startIcon={<UploadFileIcon />} disabled>
          {actionLabel}
        </Button>
      )}
    </Stack>
  );
}

export default function LogoVideoSection({ propertyId, propertyLogoId, vendorId, videoId, videoPosterImage }: Props) {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const propertyEditUrl = vendorId && propertyId ? `/vendors/${vendorId}/edit-property/${propertyId}` : undefined;
  const videoLabel = videoId ? `Vimeo video ${videoId}` : 'No video uploaded';

  useEffect(() => {
    setIsVideoPlaying(false);
  }, [videoId]);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
        gap: 3,
        mt: 4,
      }}
    >
      <Paper variant="outlined" sx={supportingAssetCardSx}>
        {renderSupportingAssetHeader('Property logo', 'Replace logo', { to: propertyEditUrl })}
        <Divider />
        <Stack spacing={2} sx={{ p: 3 }}>
          <Box
            sx={{
              ...supportingAssetMediaSx,
              display: 'grid',
              placeItems: 'center',
              border: 1,
              borderStyle: 'dashed',
              borderColor: 'grey.300',
              borderRadius: 1,
              bgcolor: 'background.default',
              p: 3,
            }}
          >
            {propertyLogoId ? (
              <Image
                publicId={propertyLogoId}
                alt="Property logo"
                options={{ width: 420 }}
                style={{ maxWidth: '100%', maxHeight: 220, objectFit: 'contain' }}
              />
            ) : (
              <Stack alignItems="center" spacing={1} color="text.secondary">
                <ImageOutlinedIcon sx={{ fontSize: 48, color: 'grey.400' }} />
                <Typography variant="body1">No logo uploaded</Typography>
              </Stack>
            )}
          </Box>
          <Typography variant="body1" color="text.secondary">
            Shown on Property information sidebar.
          </Typography>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={supportingAssetCardSx}>
        {renderSupportingAssetHeader('Video', 'Replace video')}
        <Divider />
        <Stack spacing={2} sx={{ p: 3 }}>
          <Box
            sx={{
              ...supportingAssetMediaSx,
              position: 'relative',
              overflow: 'hidden',
              borderRadius: 1,
              bgcolor: 'grey.900',
            }}
          >
            {videoId && isVideoPlaying ? (
              <Box
                component="iframe"
                title={videoLabel}
                src={`https://player.vimeo.com/video/${videoId}?autoplay=1`}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                sx={{
                  width: '100%',
                  height: '100%',
                  border: 0,
                  display: 'block',
                }}
              />
            ) : videoId && videoPosterImage ? (
              <Image
                publicId={videoPosterImage.publicId}
                alt={videoPosterImage.title || 'Video preview'}
                options={{ width: 960, height: 540 }}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            ) : videoId ? (
              <Box sx={{ width: '100%', height: '100%', bgcolor: 'grey.900' }} />
            ) : (
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{
                  width: '100%',
                  height: '100%',
                  border: 1,
                  borderColor: 'grey.300',
                  borderRadius: 1,
                  bgcolor: 'grey.50',
                  color: 'grey.400',
                }}
              >
                <VideoCallOutlinedIcon fontSize="large" />
              </Stack>
            )}
            {videoId && !isVideoPlaying && (
              <>
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    bgcolor: 'rgba(0, 0, 0, 0.42)',
                  }}
                />
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  spacing={2}
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    color: 'common.white',
                    textAlign: 'center',
                    px: 2,
                  }}
                >
                  <IconButton
                    aria-label="Play video"
                    sx={videoPlayButtonSx}
                    disabled={!videoId}
                    onClick={() => setIsVideoPlaying(true)}
                  >
                    <PlayArrowIcon sx={{ fontSize: 38 }} />
                  </IconButton>
                  <Typography
                    variant="h6"
                    sx={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {videoLabel}
                  </Typography>
                </Stack>
              </>
            )}
          </Box>
          <Typography variant="body1" color="text.secondary">
            Appended to carousel hero #2 and shown on each room gallery.
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
}

const supportingAssetCardSx = {
  overflow: 'hidden',
  minWidth: 0,
};

const supportingAssetMediaSx = {
  aspectRatio: '16 / 9',
  width: '100%',
};

const videoPlayButtonSx = {
  width: 64,
  height: 64,
  bgcolor: 'common.white',
  color: 'grey.800',
  '&:hover': {
    bgcolor: 'grey.100',
  },
  '&.Mui-disabled': {
    bgcolor: 'common.white',
    color: 'grey.500',
  },
};
